import React, { useState, useEffect, useRef } from 'react';
import { X, User, Lock, Save, Loader2, Check, AlertCircle, Link2, Unlink, Globe, Info, Camera, Upload } from 'lucide-react';
import { changePassword } from '../services/api';
import api from '../services/api';
import LanguageSelector from './LanguageSelector';
import { useI18n } from '../context/I18nContext';
import AvatarSVG from './AvatarSVG';

// Avatares predefinidos con SVG inline
const AVATAR_OPTIONS = [
  // Clasicos Masculinos
  { id: 'male1', category: 'classic', gender: 'male', label: 'Hombre 1', color: '#3B82F6' },
  { id: 'male2', category: 'classic', gender: 'male', label: 'Hombre 2', color: '#6366F1' },
  // Clasicos Femeninos
  { id: 'female1', category: 'classic', gender: 'female', label: 'Mujer 1', color: '#EC4899' },
  { id: 'female2', category: 'classic', gender: 'female', label: 'Mujer 2', color: '#D946EF' },
  // Tematicos
  { id: 'astronaut', category: 'themed', gender: 'neutral', label: 'Astronauta', color: '#1E3A8A' },
  { id: 'robot', category: 'themed', gender: 'neutral', label: 'Robot', color: '#475569' },
  { id: 'ninja', category: 'themed', gender: 'neutral', label: 'Ninja', color: '#1F2937' },
  { id: 'scientist', category: 'themed', gender: 'neutral', label: 'Cientifico', color: '#059669' },
  { id: 'artist', category: 'themed', gender: 'neutral', label: 'Artista', color: '#7C3AED' },
  { id: 'chef', category: 'themed', gender: 'neutral', label: 'Chef', color: '#DC2626' },
  { id: 'pirate', category: 'themed', gender: 'neutral', label: 'Pirata', color: '#92400E' },
  { id: 'superhero', category: 'themed', gender: 'neutral', label: 'Superheroe', color: '#B91C1C' },
  // Animales
  { id: 'cat', category: 'animal', gender: 'neutral', label: 'Gato', color: '#F97316' },
  { id: 'dog', category: 'animal', gender: 'neutral', label: 'Perro', color: '#A16207' },
  { id: 'fox', category: 'animal', gender: 'neutral', label: 'Zorro', color: '#EA580C' },
  { id: 'panda', category: 'animal', gender: 'neutral', label: 'Panda', color: '#18181B' },
];

export default function SettingsPanel({ user, onClose, onAvatarChange }) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('profile');
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [connectingGoogle, setConnectingGoogle] = useState(false);

  // Estado para avatar
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [customPhoto, setCustomPhoto] = useState(null);
  const [avatarColor, setAvatarColor] = useState('#3B82F6');
  const fileInputRef = useRef(null);

  // Estado para edición de perfil
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);

  // Cargar datos del usuario cuando cambie
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Cargar avatar guardado
  useEffect(() => {
    const savedAvatar = localStorage.getItem('userAvatar');
    const savedPhoto = localStorage.getItem('userPhoto');
    const savedColor = localStorage.getItem('avatarColor');

    if (savedPhoto) {
      setCustomPhoto(savedPhoto);
    } else if (savedAvatar) {
      setSelectedAvatar(savedAvatar);
    }
    if (savedColor) {
      setAvatarColor(savedColor);
    }
  }, []);

  // Verificar si Google Calendar esta conectado
  useEffect(() => {
    checkGoogleConnection();
  }, []);

  const checkGoogleConnection = async () => {
    try {
      const response = await api.get('/api/integrations/google/status');
      setGoogleConnected(response.data.connected);
      setGoogleEmail(response.data.email || '');
    } catch (error) {
      console.log('Google Calendar no configurado');
    }
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const response = await api.get('/api/integrations/google/auth-url');
      // Abrir ventana de autorizacion de Google
      window.open(response.data.url, 'google-auth', 'width=500,height=600');

      // Escuchar mensaje de la ventana emergente
      window.addEventListener('message', async (event) => {
        if (event.data.type === 'google-auth-success') {
          setGoogleConnected(true);
          setGoogleEmail(event.data.email);
          setConnectingGoogle(false);
        }
      }, { once: true });
    } catch (error) {
      console.error('Error conectando Google:', error);
      setMessage({ type: 'error', text: 'Error al conectar con Google Calendar' });
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('¿Desconectar Google Calendar?')) return;
    try {
      await api.post('/api/integrations/google/disconnect');
      setGoogleConnected(false);
      setGoogleEmail('');
    } catch (error) {
      console.error('Error desconectando Google:', error);
    }
  };

  // Funciones para manejo de avatar
  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar.id);
    setCustomPhoto(null);
    setAvatarColor(avatar.color);
    localStorage.setItem('userAvatar', avatar.id);
    localStorage.setItem('avatarColor', avatar.color);
    localStorage.removeItem('userPhoto');
    if (onAvatarChange) {
      onAvatarChange({ type: 'avatar', avatarId: avatar.id, color: avatar.color });
    }
    setMessage({ type: 'success', text: 'Avatar actualizado' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La imagen debe ser menor a 2MB' });
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Solo se permiten imágenes' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setCustomPhoto(base64);
      setSelectedAvatar(null);
      localStorage.setItem('userPhoto', base64);
      localStorage.removeItem('userAvatar');
      if (onAvatarChange) {
        onAvatarChange({ type: 'photo', photo: base64 });
      }
      setMessage({ type: 'success', text: 'Foto de perfil actualizada' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setCustomPhoto(null);
    localStorage.removeItem('userPhoto');
    if (onAvatarChange) {
      onAvatarChange({ type: 'default' });
    }
    setMessage({ type: 'success', text: 'Foto eliminada' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  // Obtener avatar actual para preview
  const getCurrentAvatarOption = () => {
    if (selectedAvatar) {
      return AVATAR_OPTIONS.find(a => a.id === selectedAvatar);
    }
    return null;
  };

  // Guardar datos de perfil
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put(`/api/users/${user.id}`, {
        name: profileData.name,
        email: profileData.email
      });

      setMessage({ type: 'success', text: 'Perfil actualizado correctamente' });

      // Actualizar el usuario en el contexto si es necesario (recargar página)
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Error guardando perfil:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar el perfil'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  // Guardar avatar en servidor
  const handleSaveAvatar = async () => {
    setSavingAvatar(true);
    setMessage({ type: '', text: '' });

    try {
      // Construir URL del avatar para el servidor
      let avatarUrl = null;
      if (customPhoto) {
        avatarUrl = customPhoto; // Base64 de la foto
      } else if (selectedAvatar) {
        avatarUrl = `avatar:${selectedAvatar}:${avatarColor}`; // Formato especial para avatares predefinidos
      }

      await api.put(`/api/users/${user.id}`, {
        avatar_url: avatarUrl
      });

      setMessage({ type: 'success', text: 'Avatar guardado en el servidor' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Error guardando avatar:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar el avatar'
      });
    } finally {
      setSavingAvatar(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    setLoading(true);

    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      setMessage({ type: 'success', text: 'Contraseña actualizada correctamente' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error al cambiar contraseña' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Configuracion</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition ${
              activeTab === 'profile'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Mi cuenta
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition ${
              activeTab === 'security'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Seguridad
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition ${
              activeTab === 'integrations'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Link2 className="w-4 h-4 inline mr-2" />
            Integraciones
          </button>
          <button
            onClick={() => setActiveTab('language')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition ${
              activeTab === 'language'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            {t('settings.language')}
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition ${
              activeTab === 'about'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Info className="w-4 h-4 inline mr-2" />
            Acerca de
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Mensaje de feedback */}
              {message.text && activeTab === 'profile' && (
                <div className={`flex items-center p-3 rounded-lg ${
                  message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                }`}>
                  {message.type === 'error' ? <AlertCircle className="w-5 h-5 mr-2" /> : <Check className="w-5 h-5 mr-2" />}
                  {message.text}
                </div>
              )}

              {/* Card de usuario */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center gap-6">
                  {/* Avatar grande */}
                  <div className="relative">
                    {customPhoto ? (
                      <img src={customPhoto} alt="Avatar" className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl" />
                    ) : selectedAvatar ? (
                      <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-xl">
                        <AvatarSVG type={getCurrentAvatarOption()?.gender} avatarId={selectedAvatar} color={avatarColor} size={96} />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-4xl font-bold border-4 border-white shadow-xl" style={{ backgroundColor: avatarColor }}>
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>

                  {/* Info usuario */}
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-gray-900">{user?.name}</h3>
                    <p className="text-gray-600">{user?.email}</p>
                    {user?.phone && <p className="text-gray-500 text-sm mt-1">{user.phone}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {user?.role}
                      </span>
                      <span className="text-gray-400 text-xs">@{user?.username}</span>
                    </div>
                  </div>

                  {/* Botón editar */}
                  <button
                    onClick={() => setShowEditProfileModal(true)}
                    className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    <User className="w-5 h-5" />
                    Modificar perfil
                  </button>
                </div>
              </div>

              {/* Info adicional */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-medium text-gray-700 mb-3">Resumen de cuenta</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nombre:</span>
                    <span className="ml-2 text-gray-900 font-medium">{user?.name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 text-gray-900 font-medium">{user?.email || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Telefono:</span>
                    <span className="ml-2 text-gray-900 font-medium">{user?.phone || 'No especificado'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Rol:</span>
                    <span className="ml-2 text-gray-900 font-medium capitalize">{user?.role || '-'}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 text-center">
                Haz clic en <span className="underline cursor-pointer text-blue-500" onClick={() => setShowEditProfileModal(true)}>Modificar perfil</span> para cambiar tu avatar y datos personales.
              </p>
            </div>
          )}

          {activeTab === 'security' && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <h3 className="font-medium">Cambiar contraseña</h3>

              {message.text && (
                <div className={`flex items-center p-3 rounded-lg ${
                  message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                }`}>
                  {message.type === 'error' ? (
                    <AlertCircle className="w-5 h-5 mr-2" />
                  ) : (
                    <Check className="w-5 h-5 mr-2" />
                  )}
                  {message.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña actual
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar nueva contraseña
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar cambios
                  </>
                )}
              </button>
            </form>
          )}

          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <h3 className="font-medium">Integraciones</h3>

              {/* Google Calendar */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-white border rounded-lg flex items-center justify-center mr-3">
                      <svg viewBox="0 0 24 24" className="w-6 h-6">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium">Google Calendar</h4>
                      <p className="text-sm text-gray-500">
                        {googleConnected
                          ? `Conectado: ${googleEmail}`
                          : 'Sincroniza eventos con tu calendario de Google'}
                      </p>
                    </div>
                  </div>
                  {googleConnected ? (
                    <button
                      onClick={handleDisconnectGoogle}
                      className="flex items-center px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Desconectar
                    </button>
                  ) : (
                    <button
                      onClick={handleConnectGoogle}
                      disabled={connectingGoogle}
                      className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {connectingGoogle ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      Conectar
                    </button>
                  )}
                </div>

                {googleConnected && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center text-sm text-green-600">
                      <Check className="w-4 h-4 mr-2" />
                      Los eventos del chat se sincronizaran automaticamente
                    </div>
                  </div>
                )}
              </div>

              {/* Info */}
              <p className="text-sm text-gray-500">
                Al conectar Google Calendar, los eventos que crees en el chat se sincronizaran automaticamente con tu calendario personal.
              </p>
            </div>
          )}

          {activeTab === 'language' && (
            <div className="space-y-6">
              <h3 className="font-medium">{t('settings.language')}</h3>
              <p className="text-sm text-gray-500">
                Selecciona el idioma de la interfaz:
              </p>
              <LanguageSelector variant="buttons" />
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  El idioma se guardara automaticamente y se aplicara a toda la plataforma.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'about' && (
            <div className="space-y-6">
              {/* Logo XIA */}
              <div className="flex flex-col items-center py-6">
                <img
                  src="/xia-logo.png"
                  alt="XIA"
                  className="h-32"
                />
              </div>

              {/* Info de version */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Version</span>
                  <span className="font-medium text-gray-900">2.0.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Build</span>
                  <span className="font-medium text-gray-900">2026.01.16</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Motor IA</span>
                  <span className="font-medium text-gray-900">Ollama + Llama 3.2</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Frontend</span>
                  <span className="font-medium text-gray-900">React + Tailwind</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Backend</span>
                  <span className="font-medium text-gray-900">Node.js + Express</span>
                </div>
              </div>

              {/* Empresa */}
              <div className="text-center pt-4 border-t">
                <p className="text-gray-900 font-medium">Manantial de Ideas SL</p>
                <p className="text-gray-500 text-sm">https://chatmanantial.com</p>
                <p className="text-gray-400 text-xs mt-2">&copy; 2026 Todos los derechos reservados</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Editar Perfil */}
      {showEditProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            {/* Header Modal */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Modificar Perfil</h2>
              <button onClick={() => setShowEditProfileModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenido Modal */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Mensaje de feedback */}
              {message.text && (
                <div className={`flex items-center p-3 rounded-lg ${
                  message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                }`}>
                  {message.type === 'error' ? <AlertCircle className="w-5 h-5 mr-2" /> : <Check className="w-5 h-5 mr-2" />}
                  {message.text}
                </div>
              )}

              {/* Avatar actual */}
              <div className="flex items-center space-x-6 p-4 bg-gray-50 rounded-xl">
                <div className="relative">
                  {customPhoto ? (
                    <img src={customPhoto} alt="Avatar" className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg" />
                  ) : selectedAvatar ? (
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg">
                      <AvatarSVG type={getCurrentAvatarOption()?.gender} avatarId={selectedAvatar} color={avatarColor} size={80} />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-lg" style={{ backgroundColor: avatarColor }}>
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-blue-600 transition-colors"
                    title="Subir foto"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{profileData.name || user?.name}</h3>
                  <p className="text-gray-500">{profileData.email || user?.email}</p>
                  <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {user?.role}
                  </span>
                  {customPhoto && (
                    <button onClick={handleRemovePhoto} className="ml-2 text-xs text-red-500 hover:underline">
                      Eliminar foto
                    </button>
                  )}
                </div>
              </div>

              {/* Selector de Avatar */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Elige tu avatar</h4>

                {/* Clasicos */}
                <div>
                  <p className="text-sm text-gray-500 mb-2">Clasicos</p>
                  <div className="flex flex-wrap gap-3">
                    {AVATAR_OPTIONS.filter(a => a.category === 'classic').map((avatar) => (
                      <button
                        key={avatar.id}
                        onClick={() => handleAvatarSelect(avatar)}
                        className={`w-14 h-14 rounded-xl overflow-hidden transition-all hover:scale-105 ${
                          selectedAvatar === avatar.id ? 'ring-2 ring-blue-500 ring-offset-2' : 'hover:ring-2 hover:ring-gray-300'
                        }`}
                        title={avatar.label}
                      >
                        <AvatarSVG type={avatar.gender} color={avatar.color} avatarId={avatar.id} size={56} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tematicos */}
                <div>
                  <p className="text-sm text-gray-500 mb-2">Tematicos</p>
                  <div className="flex flex-wrap gap-3">
                    {AVATAR_OPTIONS.filter(a => a.category === 'themed').map((avatar) => (
                      <button
                        key={avatar.id}
                        onClick={() => handleAvatarSelect(avatar)}
                        className={`w-14 h-14 rounded-xl overflow-hidden transition-all hover:scale-105 ${
                          selectedAvatar === avatar.id ? 'ring-2 ring-blue-500 ring-offset-2' : 'hover:ring-2 hover:ring-gray-300'
                        }`}
                        title={avatar.label}
                      >
                        <AvatarSVG avatarId={avatar.id} color={avatar.color} size={56} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Animales */}
                <div>
                  <p className="text-sm text-gray-500 mb-2">Animales</p>
                  <div className="flex flex-wrap gap-3">
                    {AVATAR_OPTIONS.filter(a => a.category === 'animal').map((avatar) => (
                      <button
                        key={avatar.id}
                        onClick={() => handleAvatarSelect(avatar)}
                        className={`w-14 h-14 rounded-xl overflow-hidden transition-all hover:scale-105 ${
                          selectedAvatar === avatar.id ? 'ring-2 ring-blue-500 ring-offset-2' : 'hover:ring-2 hover:ring-gray-300'
                        }`}
                        title={avatar.label}
                      >
                        <AvatarSVG avatarId={avatar.id} color={avatar.color} size={56} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subir foto personalizada */}
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">O sube tu propia foto</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    Subir imagen (max 2MB)
                  </button>
                </div>
              </div>

              {/* Datos del usuario */}
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium text-gray-900">Datos de usuario</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="Ej: +34 600 123 456"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    value={user?.username || ''}
                    disabled
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">El username no se puede modificar</p>
                </div>
              </div>
            </div>

            {/* Footer Modal - Botones */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowEditProfileModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveAvatar}
                disabled={savingAvatar}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {savingAvatar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Avatar
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Perfil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
